import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import { supabase } from '../supabaseClient';

const C = {
  blue: '#6699ff',
  navy: '#0b1120',
  creamWarm: '#ede5d8',
  creamPale: '#f8f5ef',
  textDark: '#0d1520',
  textBody: '#3a4860',
  textMuted: '#6b7a92',
  white: '#ffffff',
};

const TOOLBAR_GROUPS = [
  [
    { label: 'B', command: 'toggleBold', style: { fontWeight: 700 } },
    { label: 'I', command: 'toggleItalic', style: { fontStyle: 'italic' } },
    { label: 'U', command: 'toggleUnderline', style: { textDecoration: 'underline' } },
    { label: 'S', command: 'toggleStrike', style: { textDecoration: 'line-through' } },
    { label: 'H', command: 'toggleHighlight' },
  ],
  [
    { label: 'H1', command: 'toggleHeading', args: { level: 1 } },
    { label: 'H2', command: 'toggleHeading', args: { level: 2 } },
    { label: 'H3', command: 'toggleHeading', args: { level: 3 } },
  ],
  [
    { label: '•', command: 'toggleBulletList' },
    { label: '1.', command: 'toggleOrderedList' },
    { label: '""', command: 'toggleBlockquote' },
    { label: '—', command: 'setHorizontalRule' },
  ],
  [
    { label: 'L', command: 'setTextAlign', args: 'left', style: { fontSize: 11 } },
    { label: 'C', command: 'setTextAlign', args: 'center', style: { fontSize: 11 } },
    { label: 'R', command: 'setTextAlign', args: 'right', style: { fontSize: 11 } },
  ],
];

export default function RichTextEditor({ content, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({ placeholder: 'Start writing your article...' }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false }),
      Highlight,
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getJSON());
    },
  });

  if (!editor) return null;

  const addImage = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp,image/gif';

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        alert('Image must be under 5MB');
        return;
      }

      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        alert('Only JPG, PNG, WebP, and GIF images are allowed');
        return;
      }

      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${Date.now()}_${safeName}`;

      const { error } = await supabase.storage
        .from('article-images')
        .upload(filePath, file, { contentType: file.type });

      if (error) {
        alert('Upload failed: ' + error.message);
        return;
      }

      const { data: urlData } = supabase.storage
        .from('article-images')
        .getPublicUrl(filePath);

      editor.chain().focus().setImage({ src: urlData.publicUrl }).run();
    };

    input.click();
  };

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url, target: '_blank' }).run();
    }
  };

  const isActive = (command, args) => {
    if (command === 'toggleHeading') return editor.isActive('heading', args);
    if (command === 'toggleBold') return editor.isActive('bold');
    if (command === 'toggleItalic') return editor.isActive('italic');
    if (command === 'toggleUnderline') return editor.isActive('underline');
    if (command === 'toggleStrike') return editor.isActive('strike');
    if (command === 'toggleHighlight') return editor.isActive('highlight');
    if (command === 'toggleBulletList') return editor.isActive('bulletList');
    if (command === 'toggleOrderedList') return editor.isActive('orderedList');
    if (command === 'toggleBlockquote') return editor.isActive('blockquote');
    if (command === 'setTextAlign') return editor.isActive({ textAlign: args });
    return false;
  };

  const runCommand = (command, args) => {
    if (args) {
      editor.chain().focus()[command](args).run();
    } else {
      editor.chain().focus()[command]().run();
    }
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap',
        padding: '10px 12px', background: C.creamPale, border: `1px solid ${C.creamWarm}`,
        borderBottom: 'none',
      }}>
        {TOOLBAR_GROUPS.map((group, gi) => (
          <div key={gi} style={{ display: 'flex', gap: 2, marginRight: 8 }}>
            {group.map(btn => (
              <button
                key={btn.label}
                onClick={() => runCommand(btn.command, btn.args)}
                style={{
                  width: 32, height: 32, border: `1px solid ${isActive(btn.command, btn.args) ? C.blue : 'transparent'}`,
                  background: isActive(btn.command, btn.args) ? 'rgba(102,153,255,0.1)' : 'transparent',
                  color: isActive(btn.command, btn.args) ? C.blue : C.textBody,
                  fontFamily: "'Manrope', sans-serif", fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 2, transition: 'all 0.15s ease',
                  ...(btn.style || {}),
                }}
              >
                {btn.label}
              </button>
            ))}
          </div>
        ))}

        <div style={{ display: 'flex', gap: 2, marginRight: 8 }}>
          <button onClick={addLink} style={{
            height: 32, padding: '0 10px', border: `1px solid ${editor.isActive('link') ? C.blue : 'transparent'}`,
            background: editor.isActive('link') ? 'rgba(102,153,255,0.1)' : 'transparent',
            color: editor.isActive('link') ? C.blue : C.textBody,
            fontFamily: "'Manrope', sans-serif", fontSize: 11, fontWeight: 600,
            cursor: 'pointer', borderRadius: 2,
          }}>
            Link
          </button>
          <button onClick={addImage} style={{
            height: 32, padding: '0 10px', border: '1px solid transparent',
            background: 'transparent', color: C.textBody,
            fontFamily: "'Manrope', sans-serif", fontSize: 11, fontWeight: 600,
            cursor: 'pointer', borderRadius: 2,
          }}>
            Image
          </button>
        </div>
      </div>

      {/* Editor */}
      <div style={{
        border: `1px solid ${C.creamWarm}`, background: C.white,
        minHeight: 400,
      }}>
        <EditorContent editor={editor} />
      </div>

      {/* Editor Styles */}
      <style>{`
        .tiptap {
          padding: 24px 28px;
          outline: none;
          min-height: 400px;
          font-family: 'Manrope', sans-serif;
          font-size: 15px;
          line-height: 1.8;
          color: ${C.textDark};
        }
        .tiptap h1 { font-family: 'Cormorant Garamond', serif; font-size: 32px; font-weight: 600; margin: 24px 0 12px; color: ${C.navy}; }
        .tiptap h2 { font-family: 'Cormorant Garamond', serif; font-size: 26px; font-weight: 600; margin: 20px 0 10px; color: ${C.navy}; }
        .tiptap h3 { font-family: 'Cormorant Garamond', serif; font-size: 22px; font-weight: 600; margin: 16px 0 8px; color: ${C.navy}; }
        .tiptap p { margin: 0 0 12px; }
        .tiptap ul, .tiptap ol { padding-left: 24px; margin: 0 0 12px; }
        .tiptap li { margin: 4px 0; }
        .tiptap blockquote {
          border-left: 3px solid ${C.blue};
          padding-left: 20px;
          margin: 16px 0;
          color: ${C.textBody};
          font-style: italic;
        }
        .tiptap img {
          max-width: 100%;
          height: auto;
          margin: 16px 0;
          border: 1px solid ${C.creamWarm};
        }
        .tiptap a { color: ${C.blue}; text-decoration: underline; }
        .tiptap hr { border: none; border-top: 1px solid ${C.creamWarm}; margin: 24px 0; }
        .tiptap mark { background: rgba(102,153,255,0.2); padding: 1px 4px; }
        .tiptap p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: ${C.textMuted};
          pointer-events: none;
          height: 0;
        }
      `}</style>
    </div>
  );
}